import { useState } from "react";
import { X, AlertTriangle, Trash2, Loader2 } from "lucide-react";

/**
 * DeleteConfirmDialog — heavy confirmation with a type-DELETE gate.
 * Use for irreversible-feeling actions (even though the backend soft-
 * deletes into the Recycle Bin, we want an extra pause on the primary
 * flows so accidental clicks always take a second thought).
 *
 * Props:
 *   open        : boolean
 *   title       : short heading — e.g. "Delete this booking?"
 *   description : one-liner explaining what disappears
 *   label       : optional row label rendered inside a card
 *   onCancel    : () => void
 *   onConfirm   : () => Promise<void>  (called only after DELETE is typed)
 *   confirmWord : override the required word (default "DELETE")
 *   restoreHint : extra note on where the item can be recovered
 */
export default function DeleteConfirmDialog({
  open,
  title = "Confirm delete",
  description = "This will remove the record. You can restore it from the Recycle Bin (Founder-only).",
  label = "",
  onCancel,
  onConfirm,
  confirmWord = "DELETE",
  restoreHint = "Founders can restore this from the Recycle Bin within 60 days.",
}) {
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState(false);

  if (!open) return null;

  const ready = typed.trim().toUpperCase() === confirmWord.toUpperCase();

  const doConfirm = async () => {
    if (!ready || busy) return;
    setBusy(true);
    try {
      await onConfirm();
      setTyped("");
    } finally {
      setBusy(false);
    }
  };

  const close = () => {
    if (busy) return;
    setTyped("");
    onCancel?.();
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={close} data-testid="delete-confirm-backdrop">
      <div className="bg-white max-w-md w-full rounded-lg shadow-xl" onClick={(e) => e.stopPropagation()} data-testid="delete-confirm-dialog">
        <div className="p-5 border-b border-[color:var(--aii-border)] flex items-start justify-between gap-3">
          <div className="flex items-start gap-2">
            <AlertTriangle className="text-red-600 mt-0.5 shrink-0" size={20}/>
            <div>
              <h3 className="font-display font-semibold text-lg text-red-700">{title}</h3>
              <p className="text-sm text-[color:var(--aii-text-2)] mt-1">{description}</p>
            </div>
          </div>
          <button onClick={close} className="p-1 text-[color:var(--aii-muted)] hover:text-[color:var(--aii-text)]"><X size={16}/></button>
        </div>
        <div className="p-5 space-y-4">
          {label && (
            <div className="border border-[color:var(--aii-border)] rounded p-3 bg-[color:var(--aii-bg)] text-sm font-medium break-words" data-testid="delete-confirm-label">
              {label}
            </div>
          )}
          <div>
            <label className="text-xs text-[color:var(--aii-muted)] mb-1 block">
              Type <span className="font-mono font-semibold text-red-700">{confirmWord}</span> to confirm
            </label>
            <input
              autoFocus
              data-testid="delete-confirm-input"
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              placeholder={confirmWord}
              className="w-full px-3 py-2 border border-[color:var(--aii-border)] rounded-md text-sm font-mono focus:ring-2 focus:ring-red-400/30 focus:border-red-500"
              onKeyDown={(e) => { if (e.key === "Enter" && ready) doConfirm(); }}
            />
          </div>
          {restoreHint && (
            <div className="text-[11px] text-[color:var(--aii-muted)] leading-relaxed">{restoreHint}</div>
          )}
        </div>
        <div className="p-4 border-t border-[color:var(--aii-border)] flex justify-end gap-2">
          <button onClick={close} disabled={busy}
            className="text-sm px-3 py-1.5 border border-[color:var(--aii-border)] rounded hover:bg-[color:var(--aii-green-tint)] disabled:opacity-50">
            Cancel
          </button>
          <button
            data-testid="delete-confirm-submit"
            onClick={doConfirm}
            disabled={!ready || busy}
            className="text-sm px-3 py-1.5 bg-red-600 text-white rounded hover:bg-red-700 flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {busy ? <><Loader2 size={13} className="animate-spin"/> Deleting…</> : <><Trash2 size={13}/> Delete</>}
          </button>
        </div>
      </div>
    </div>
  );
}
